import { NetworkStatus } from '@apollo/client';
import type { QueryHookOptions, QueryResult } from '@apollo/client';
import type { GetMeQuery, GetMeQueryVariables } from 'generated/graphql';
import { UserAnalytics } from 'modules/analytics/analytics';
import { WithApolloContext } from 'modules/Apollo/types';
import { useMemo } from 'react';
import { useCurrentUserQueryContext } from './CurrentUserQueryContext';

type CurrentUserQueryOptions = Pick<
  QueryHookOptions<GetMeQuery, GetMeQueryVariables>,
  'context' | 'skip'
>;

export function useCurrentUser(
  options?: CurrentUserQueryOptions
): QueryResult<GetMeQuery, GetMeQueryVariables> {
  const context = useCurrentUserQueryContext();

  if (options?.skip !== true) return context.query;

  return {
    ...context.query,
    called: false,
    data: undefined,
    error: undefined,
    loading: false,
    networkStatus: NetworkStatus.ready,
  };
}

export function useUserAnalytics({ context }: WithApolloContext): UserAnalytics | null {
  const { data } = useCurrentUser({ context });

  return useMemo(() => {
    if (data == null) return null;

    const {
      me: { id, firstName, lastName, email, clinic },
    } = data;

    return {
      id: id,
      email: email,
      name: `${firstName} ${lastName}`,
      clinicName: clinic?.name ?? 'no clinic',
      clinicSmid: clinic?.smid ?? 'no clinic',
    };
  }, [data]);
}
